import type { ChatFlowStrategy, FlowContext } from './types';
import { analysisAsync, analysisStatus, analysisReport } from '@/services';
import { useChatStore } from '@/store';
import { ERole, IProgressHistoryItem, MessageStatus, TAnalysisStatus } from '@/types';
import { t } from '@/i18n';
import { abortableSleep } from '@/utils';

const POLL_INTERVAL = 5000; // 5 秒轮询一次
const ACTIVE_ANALYSIS_STATUS: TAnalysisStatus[] = ['queued', 'running'];

function normalizeTaskLine(text?: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

function buildProgressMarkdown(lines: string[]): string {
  return lines.map((line) => `- ${line}`).join('\n');
}

function updateProgressMessage(
  updateMessage: ReturnType<typeof useChatStore.getState>['updateMessage'],
  msgId: string,
  progressText: string,
) {
  updateMessage(msgId, {
    content: { progressText, progressStatus: 'running' },
    status: 'running',
  });
}

/**
 * 从进度历史数组转换为进度行数组
 */
function getProgressLines(progressHistory: IProgressHistoryItem[]): string[] {
  return progressHistory
    .map((item) => normalizeTaskLine(item.message))
    .filter(Boolean);
}

/**
 * 轮询分析状态直到完成
 */
async function pollAnalysisStatus({ taskId, turnId, sessionId, aiMsgId, onHasReport, signal }: {
  taskId: string;
  turnId: string;
  sessionId: string;
  // updateMessage: ReturnType<typeof useChatStore.getState>['updateMessage'];
  aiMsgId: string;
  onHasReport?: () => void | Promise<void>;
  signal?: AbortSignal;
}
): Promise<{
  hasReport: boolean;
  progressHistory: IProgressHistoryItem[];
}> {
  const { updateMessage, getMessage } = useChatStore.getState();
  let status: TAnalysisStatus = 'queued';
  let hasReport = false;
  let hasReported = false;
  let progressHistory: IProgressHistoryItem[] = [];
  let pollCount = 0;
  const maxPolls = 600; // 最多轮询 600 次，约 50 分钟

  while (
    ACTIVE_ANALYSIS_STATUS.includes(status) &&
    pollCount < maxPolls &&
    !signal?.aborted
  ) {
    await abortableSleep(POLL_INTERVAL, signal);
    pollCount++;

    try {
      const statusRes = await analysisStatus({
        taskId,
        turnId,
        sessionId,
        signal,
      });
      status = statusRes.status;
      hasReport = statusRes.hasReport;
      if (statusRes.progressHistory?.length) {
        progressHistory = statusRes.progressHistory;
      }

      if (hasReport && !hasReported) {
        hasReported = true;
        onHasReport?.();
      }
    } catch (pollError) {
      if (signal?.aborted) throw pollError;
      console.warn('analysisStatus poll error, retrying...', pollError);
      continue;
    }

    // 任务已结束，或报告已经展示完：不要再写成「正在处理中」。
    const currentMsg = getMessage(aiMsgId);
    if (!ACTIVE_ANALYSIS_STATUS.includes(status) || currentMsg?.status === 'success') {
      break;
    }

    if (!progressHistory.length) {
      progressHistory = [{ message: t('chat.running') }];
    }
    const progressLines = getProgressLines(progressHistory);
    updateMessage(aiMsgId, {
      content: { progressText: buildProgressMarkdown(progressLines), progressStatus: 'running' },
      ...(currentMsg && !['streaming', 'success'].includes(currentMsg.status) ? {
        status: 'running'
      } : {})
    });
  }

  const currentMsg = getMessage(aiMsgId);
  const progressStatus = signal?.aborted || status === 'cancelled'
    ? 'cancelled'
    : status === 'failed'
      ? 'failed'
      : 'succeeded';
  const placeholderOnly = !progressHistory.some((item) => {
    const line = normalizeTaskLine(item.message);
    return Boolean(line) && line !== t('chat.running');
  });
  const settleStatus: MessageStatus | undefined =
    signal?.aborted || status === 'cancelled'
      ? 'cancelled'
      : status === 'failed' && currentMsg?.status !== 'success'
        ? 'error'
        : status === 'succeeded' && currentMsg && ['sending', 'running', 'streaming'].includes(currentMsg.status) && Boolean(currentMsg.content?.text)
          ? 'success'
          : undefined;

  updateMessage(aiMsgId, {
    content: {
      progressStatus,
      ...(progressStatus === 'succeeded' && placeholderOnly ? { progressText: '' } : {}),
    },
    status: settleStatus,
  });

  return { hasReport, progressHistory };
}

/**
 * 处理分析结果
 */
function handleAnalysisResult(
  hasReport: boolean,
  progressHistory: IProgressHistoryItem[],
  updateMessage: ReturnType<typeof useChatStore.getState>['updateMessage'],
  aiMsgId: string,
): { shouldStream: boolean; prefixContent?: string } {
  if (hasReport) {
    const progressLines = getProgressLines(progressHistory);
    return {
      shouldStream: true,
      prefixContent: buildProgressMarkdown(progressLines),
    };
  } else {
    updateMessage(aiMsgId, {
      statusText: t('chat.analysisFailed'),
      status: 'error',
    });
  }
  // else if (status === 'cancelled') {
  //   updateMessage(aiMsgId, {
  //     status: 'cancelled',
  //     statusText: t('chat.analysisCancelled'),
  //   });
  // } else {
  //   updateMessage(aiMsgId, {
  //     statusText: t('chat.analysisTimeout'),
  //     status: 'error',
  //   });
  // }

  return { shouldStream: false };
}

/**
 * 更新消息为错误或取消状态
 */
function updateMessageWithError(
  updateMessage: ReturnType<typeof useChatStore.getState>['updateMessage'],
  msgId: string,
  isAborted: boolean,
) {
  updateMessage(msgId, {
    content: { progressStatus: isAborted ? 'cancelled' : 'failed' },
    status: isAborted ? 'cancelled' : 'error',
    statusText: isAborted ? t('chat.analysisCancelled') : t('chat.answerFailed'),
  });
}

/**
 * Business Analysis 流程：
 * 1. 调用 analysisAsync 发起分析
 * 2. 轮询 analysisStatus 直到完成
 * 3. 根据结果：
 *    - succeeded: 调用 analysisReport 获取报告（SSE 流式）
 *    - failed/cancelled: 直接返回 current_task_desc
 */
export const businessAnalysisFlow: ChatFlowStrategy = {
  async sendMessage(question: string, context: FlowContext) {
    const {
      addMessage,
      currentRole,
      updateMessage,
      setLoading,
      setQuickQuestions,
      setTaskId,
      setTurnId,
      sessionInfo,
      setSessionInfo
    } = useChatStore.getState();
    const bossSessionId = sessionInfo?.type === 'boss-report' ? sessionInfo.sessionId : undefined;

    addMessage({
      role: 'user',
      type: 'business-analysis',
      bizPayload: {
        role: currentRole.id,
        sessionId: bossSessionId,
      },
      content: { text: question },
      status: 'success',
    });

    const aiMsgId = addMessage({
      role: 'assistant',
      type: 'business-analysis',
      bizPayload: {
        role: currentRole.id,
        sessionId: bossSessionId,
      },
      content: { text: '' },
      status: 'sending',
    });

    setLoading(true);
    setQuickQuestions([]);

    try {
      // 1. 发起分析请求
      let sessionIdForRequest = context.sessionId || bossSessionId || undefined;
      const asyncRes = await analysisAsync(question, sessionIdForRequest, context.signal);
      const taskId = asyncRes.taskId;
      setTaskId(taskId);

      const turnId = asyncRes.turnId;
      setTurnId(turnId);

      // 从第一次会话中获取sessionId并保存，用于后续的轮询和报告请求
      const sessionId = asyncRes.sessionId;
      setSessionInfo({ type: 'boss-report', sessionId });
      sessionIdForRequest = sessionId;

      // 动态更新turnId和sessionId;
      updateMessage(aiMsgId, { bizPayload: { role: currentRole.id, turnId, sessionId } })

      const onHasReport = () => {
        // 调用 analysisReport 获取报告（SSE 流式）
        handleReportStream({
          taskId,
          turnId,
          msgId: aiMsgId,
          signal: context.signal,
          sessionId: sessionIdForRequest,
          // prefixContent: resultHandler.prefixContent,
        });
      }

      // 2. 轮询 analysisStatus 直到完成
      const pollResult = await pollAnalysisStatus({
        taskId,
        turnId,
        sessionId,
        // updateMessage,
        aiMsgId,
        onHasReport,
        signal: context.signal,
      });

      // 3. 根据结果处理
      // const resultHandler = handleAnalysisResult(
      //   pollResult.hasReport,
      //   pollResult.progressHistory,
      //   updateMessage,
      //   aiMsgId,
      // );

      // if (resultHandler.shouldStream) {
      //   // 调用 analysisReport 获取报告（SSE 流式）
      //   await handleReportStream({
      //     taskId,
      //     turnId,
      //     msgId: aiMsgId,
      //     signal: context.signal,
      //     sessionId: sessionIdForRequest,
      //     prefixContent: resultHandler.prefixContent,
      //   });
      // }
    } catch (error) {
      const isAborted = context.signal?.aborted;
      updateMessageWithError(updateMessage, aiMsgId, !!isAborted);
      if (isAborted) return;

      console.error(`${ERole.BUSINESS_ANALYSIS} flow error:`, error);
    } finally {
      // setTaskId(null);
      setLoading(false);
    }
  },
};

/**
 * 处理 SSE 流式报告输出
 * 将 EventSource 的数据流式推送到对话中
 */
async function handleReportStream({ taskId, turnId, msgId, signal, sessionId, prefixContent }: {
  taskId: string,
  turnId?: string | null,
  msgId: string,
  signal?: AbortSignal,
  sessionId: string,
  prefixContent?: string,
}) {
  const { setStreaming, updateMessage, setLoading } = useChatStore.getState();
  setLoading(true);
  setStreaming(true);

  const eventSource = await analysisReport({ taskId, sessionId, turnId, signal });

  let accumulatedContent = '';
  // const stablePrefix = (prefixContent || '').trim();

  await new Promise<void>((resolve) => {
    let finished = false;

    const finish = (status: MessageStatus) => {
      if (finished) return;
      finished = true;
      setStreaming(false);
      setLoading(false);
      eventSource.close();

      const updates: Partial<Parameters<typeof updateMessage>[1]> = {
        status: status || 'error',
      };
      if (status === 'success') {
        updates.content = { progressStatus: 'succeeded' };
      } else if (status === 'error') {
        updates.content = { progressStatus: 'failed' };
      } else if (status === 'cancelled') {
        updates.content = { progressStatus: 'cancelled' };
      }
      if (status === 'error') {
        updates.statusText = t('chat.analysisFailed');
      } else if (status === 'cancelled') {
        updates.statusText = t('chat.analysisCancelled');
      }

      updateMessage(msgId, updates);
      resolve();
    };

    signal?.addEventListener('abort', () => finish('cancelled'), { once: true });

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        // 检查是否是流结束标记 (为了避免没有detal数据显示，最后要用 reportText 覆盖一次)
        if (data.done === true || data.finished === true) {
          const reportText = typeof data.reportText === 'string' ? data.reportText : '';
          updateMessage(msgId, {
            content: {
              text: reportText || accumulatedContent,
            },
            status: 'success',
          });
          finish('success');
          return;
        }

        const chunk = data.text || '';
        if (chunk) {
          accumulatedContent += chunk;
          // 实时更新消息内容
          updateMessage(msgId, {
            content: {
              // progressText: stablePrefix || undefined,
              text: accumulatedContent,
            },
            status: 'streaming',
          });
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e);
      }
    };

    eventSource.onerror = (e) => {
      if (signal?.aborted) {
        finish('cancelled');
        return;
      }
      console.error('SSE error:', e);
      finish('error');
    };
  });
}
